import React, { useCallback, useEffect, useMemo, useState } from "react";
import clsx from "clsx";
import { TailSpin } from "react-loader-spinner";
import Cookies from "js-cookie";
import axios from "axios";
import { FiEye } from "react-icons/fi";
import { CalendarDays, Flag, Globe, MapPin, FlaskConical } from "lucide-react";

import { getApi } from "../../../utils/api.js";
import { API_POLICY, API_GET_EMPLOYEE_FILE } from "../../../config/Endpoints.js";
import { displayFormatDate } from "../../../utils/helpers.js";
import DocumentViewer from "../../../components/DocumentViewer.jsx";
import PageShell from "../../../v2/components/shared/page-shell/page-shell.tsx";
import { Card } from "../../../v2/components/shared/card/card.tsx";
import { PoliciesHero } from "../../../v2/components/features/policies/components/policies-hero/policies-hero.tsx";
import { EmptyStateMessage } from "../../../v2/components/shared/empty-state-message/empty-state-message.tsx";
import {
  ResponsiveDataTable,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../../../v2/components/ui/index.ts";
import {
  normalizeRegion,
  REGION_TABS,
} from "../../../v2/components/features/holidays/constants.ts";
import { RegionBadge } from "../../../v2/components/features/holidays/components/region-badge/region-badge.tsx";
import styles from "./view-policy.module.css";
import { useSqeLabs } from "../../../v2/hooks/use-sqe-labs.ts";

const PAGE_SIZE = 200;
const SQE_REGION = "SQE";
const ViewPolicy = ({ title, description }) => {
  const isSQERestrictedView = useSqeLabs();
  const [policyList, setPolicyList] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [regionTab, setRegionTab] = useState(
    isSQERestrictedView ? SQE_REGION : "",
  );
  const [documentDetail, setDocumentDetail] = useState({
    url: null,
    type: null,
    name: null,
  });

  const accessToken = Cookies.get("accessToken");
  const visibleRegionTabs = isSQERestrictedView
    ? REGION_TABS.filter((tab) => tab.id === SQE_REGION)
    : REGION_TABS;

  const fetchList = async () => {
    setIsLoading(true);
    try {
      const firstResponse = await getApi(
        `${API_POLICY}?pageSize=${PAGE_SIZE}&page=1`,
        accessToken,
      );

      if (firstResponse.status !== 200) {
        setPolicyList([]);
        return;
      }

      const firstData = firstResponse?.data?.data ?? {};
      const firstRows = Array.isArray(firstData?.data) ? firstData.data : [];
      const totalPages = Number(firstData?.totalPages ?? 1);

      if (totalPages <= 1) {
        setPolicyList(firstRows);
        return;
      }

      const remainingRequests = Array.from({ length: totalPages - 1 }, (_, idx) =>
        getApi(`${API_POLICY}?pageSize=${PAGE_SIZE}&page=${idx + 2}`, accessToken),
      );
      const remainingResponses = await Promise.all(remainingRequests);

      const remainingRows = remainingResponses.flatMap((res) =>
        res.status === 200 && Array.isArray(res?.data?.data?.data)
          ? res.data.data.data
          : [],
      );

      setPolicyList([...firstRows, ...remainingRows]);
    } catch (e) {
      console.log(e);
      setPolicyList([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchList();
  }, []);

  useEffect(() => {
    if (documentDetail.url) {
      const openModalButton = document.getElementById("open-document-view");
      if (openModalButton) openModalButton.click();
    }
  }, [documentDetail.url]);

  useEffect(() => {
    return () => {
      if (documentDetail.url) {
        URL.revokeObjectURL(documentDetail.url);
      }
    };
  }, [documentDetail.url]);

  const handleViewFile = useCallback(async (fullPath, name) => {
    if (!fullPath) return;
    const filepath = fullPath.split("/").slice(3).join("/");
    setIsLoading(true);
    try {
      const res = await axios.get(API_GET_EMPLOYEE_FILE, {
        headers: { Authorization: `Bearer ${accessToken}` },
        params: { filepath },
        responseType: "blob",
      });

      if (res.status === 200) {
        const mimeType = res?.data?.type || "application/pdf";
        const fileBlob = new Blob([res.data], { type: mimeType });
        const fileURL = window.URL.createObjectURL(fileBlob);
        if (documentDetail.url) {
          URL.revokeObjectURL(documentDetail.url);
        }

        setDocumentDetail({
          url: fileURL,
          type: mimeType,
          name: name || "Document",
        });
      }
    } catch (error) {
      console.error("Error fetching file:", error);
    } finally {
      setIsLoading(false);
    }
  }, [accessToken, documentDetail.url]);

  const normalizedPolicies = useMemo(
    () =>
      policyList.map((item) => ({
        ...item,
        normalizedRegion: normalizeRegion(item.shiftType),
      })),
    [policyList],
  );

  const tabFilteredPolicies = useMemo(() => {
    return normalizedPolicies.filter((item) => item.normalizedRegion === regionTab);
  }, [regionTab, normalizedPolicies]);

  const stats = useMemo(() => {
    const known = normalizedPolicies.filter((item) => item.normalizedRegion != null);
    const total = known.length;
    const india = known.filter((item) => item.normalizedRegion === "INDIA").length;
    const us = known.filter((item) => item.normalizedRegion === "US").length;
    const uk = known.filter((item) => item.normalizedRegion === "UK").length;
    const sqe = known.filter((item) => item.normalizedRegion === "SQE").length;

    return { total, india, us, uk, sqe };
  }, [normalizedPolicies]);

  useEffect(() => {
    if (isSQERestrictedView) {
      setRegionTab(SQE_REGION);
      return;
    }

    if (!normalizedPolicies.length) return;

    const counts = normalizedPolicies.reduce((acc, item) => {
      if (!item.normalizedRegion) return acc;

      acc[item.normalizedRegion] =
        (acc[item.normalizedRegion] || 0) + 1;

      return acc;
    }, {});

    const maxRegion = Object.entries(counts).sort(
      (a, b) => b[1] - a[1]
    )[0]?.[0];

    if (maxRegion) {
      setRegionTab(maxRegion);
    }
  }, [normalizedPolicies, isSQERestrictedView]);

  const tableColumns = useMemo(
    () => [
      {
        id: "title",
        header: "Title",
        accessor: "title",
        sortable: true,
        // truncate: true,
        getTitleText: (row) => String(row.title ?? ""),
      },
      {
        id: "region",
        header: "Region",
        sortable: true,
        getSortValue: (row) => String(row.normalizedRegion ?? ""),
        render: (row) =>
          row.normalizedRegion ? (
            <RegionBadge region={row.normalizedRegion} />
          ) : (
            "-"
          ),
      },
      {
        id: "validFrom",
        header: "Valid From",
        sortable: true,
        getSortValue: (row) => new Date(row.validFrom).getTime(),
        render: (row) => displayFormatDate(row.validFrom),
      },
      {
        id: "description",
        header: "Description",
        sortable: true,
        getSortValue: (row) => String(row.description ?? ""),
        render: (row) => row.description || "-",
        truncate: true,
        getTitleText: (row) => String(row.description ?? ""),
      },
      {
        id: "document",
        header: "Document",
        sortable: false,
        render: (row) =>
          row.documentPath ? (
            <button
              type="button"
              className="action-btn action-btn--view"
              onClick={() =>
                handleViewFile(row.documentPath, row.documentName || row.title)
              }
            >
              <FiEye size={14} />
              View
            </button>
          ) : (
            "-"
          ),
      },
    ],
    [handleViewFile],
  );

  const statTiles = [
    {
      key: "total",
      label: "Total policies",
      value: stats.total,
      icon: CalendarDays,
      iconClass: styles.iconPurple,
    },
    {
      key: "india",
      label: "India",
      value: stats.india,
      icon: MapPin,
      iconClass: styles.iconGreen,
    },
    {
      key: "us",
      label: "US",
      value: stats.us,
      icon: Flag,
      iconClass: styles.iconBlue,
    },
    {
      key: "uk",
      label: "UK",
      value: stats.uk,
      icon: Globe,
      iconClass: styles.iconAmber,
    },
    {
      key: "sqe",
      label: "SQE Labs",
      value: stats.sqe,
      icon: FlaskConical,
      iconClass: styles.iconTeal,
    },
  ];

  return (
    <PageShell>
      {isLoading ? (
        <div className="overlay">
          <div className="loader-container">
            <TailSpin
              height="100"
              width="100"
              radius="1"
              color="blue"
              ariaLabel="tail-spin-loading"
              visible
            />
          </div>
        </div>
      ) : null}

      <PoliciesHero title={title} subtitle={description} />

      {!isSQERestrictedView && (
        <div className={styles.statsGrid}>
          {statTiles.map(({ key, label, value, icon: Icon, iconClass }) => (
            <Card key={key} className={styles.statCard} padding="3xl" variant="default">
              <div className={styles.statInner}>
                <span className={clsx(styles.iconWrap, iconClass)} aria-hidden>
                  <Icon size={20} strokeWidth={2} />
                </span>
                <div className={styles.statText}>
                  <p className={clsx("sub-heading-xs", styles.statLabel)}>{label}</p>
                  <p className={clsx("heading-lg", styles.statValue)}>{value}</p>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      <Card className={styles.tableCard} padding="5xl">
        <div className={styles.tableCardHeader}>
          <div className={styles.headerRow}>
            <h2 className={clsx("heading-sm", styles.tableTitle)}>Policies</h2>

          </div>
        </div>

        <Tabs value={regionTab} onValueChange={setRegionTab}>
          <TabsList className={styles.tabsList}>
            {visibleRegionTabs.map((tab) => (
              <TabsTrigger key={tab.id} value={tab.id} className={styles.tabTrigger}>
                {tab.label}
              </TabsTrigger>
            ))}
          </TabsList>
          <TabsContent value={regionTab} className={styles.tabPanel}>
            <ResponsiveDataTable
              columns={tableColumns}
              data={tabFilteredPolicies}
              aria-label="Policies"
              serialNumber
              pagination={false}
              emptyState={
                <EmptyStateMessage
                  message={`No Records Available`}
                />
              }
            />
          </TabsContent>
        </Tabs>
      </Card>

      <button
        id="open-document-view"
        data-bs-toggle="modal"
        data-bs-target="#staticBackdropDocumentViewer"
        style={{ display: "none" }}
      ></button>

      {documentDetail.url && (
        <DocumentViewer
          url={documentDetail.url}
          contentType={documentDetail.type}
          fileName={documentDetail.name}
        />
      )}

      {/* <Footer /> */}
    </PageShell>
  );
};

export default ViewPolicy;
